import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { QrCode, Plus, Trash2, Clock, Shield, Copy, Download, Share2, MessageCircle } from 'lucide-react';
import Layout from '../components/Layout';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DoctorAccessPage = () => {
  const { t, language } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedAccess, setSelectedAccess] = useState(null);
  const [formData, setFormData] = useState({
    doctor_name: '',
    doctor_email: '',
    expiry_hours: 24,
    access_sections: ['medical_history', 'prescriptions', 'reports', 'diary', 'referrals']
  });

  const accessSections = [
    { id: 'medical_history', label: t('medicalHistory') },
    { id: 'prescriptions', label: t('prescriptions') },
    { id: 'reports', label: t('reports') },
    { id: 'diary', label: t('diary') },
    { id: 'referrals', label: t('referrals') },
  ];

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API_URL}/doctor-access/list`);
      setEntries(response.data);
    } catch (error) {
      toast.error('Failed to load access codes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/doctor-access/generate`, formData);
      toast.success(t('accessGenerated'));
      setDialogOpen(false);
      setSelectedAccess(response.data);
      setQrDialogOpen(true);
      setFormData({
        doctor_name: '',
        doctor_email: '',
        expiry_hours: 24,
        access_sections: ['medical_history', 'prescriptions', 'reports', 'diary', 'referrals']
      });
      fetchEntries();
    } catch (error) {
      toast.error('Failed to generate access code');
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this access?')) return;
    try {
      await axios.delete(`${API_URL}/doctor-access/${id}`);
      toast.success(t('accessRevoked'));
      fetchEntries();
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };

  const toggleSection = (sectionId) => {
    setFormData(prev => ({
      ...prev,
      access_sections: prev.access_sections.includes(sectionId)
        ? prev.access_sections.filter(s => s !== sectionId)
        : [...prev.access_sections, sectionId]
    }));
  };

  const copyAccessCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Access code copied!');
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  const formatExpiry = (expiresAt) => {
    const date = new Date(expiresAt);
    const now = new Date();
    const diff = date - now;
    
    if (diff < 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) return `${hours}h ${mins}m remaining`;
    return `${mins}m remaining`;
  };

  const getPortalUrl = (code) => {
    return `${window.location.origin}/doctor-portal?code=${code}`;
  };

  return (
    <Layout>
      <div className="space-y-6 pb-nav">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-[#0D2340]/10">
              <QrCode className="h-6 w-6 text-[#0D2340]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#0D2340] font-['Manrope']">{t('doctorAccess')}</h1>
              <p className="text-slate-500">{entries.length} {t('activeAccess')}</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0D2340]" data-testid="generate-access-btn">
                <Plus className="h-4 w-4 mr-2" />
                {t('generateQR')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">{t('generateQR')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>{t('doctorName')}</Label>
                  <Input
                    value={formData.doctor_name}
                    onChange={(e) => setFormData({ ...formData, doctor_name: e.target.value })}
                    placeholder="Dr. Sita Sharma"
                    required
                    data-testid="access-doctor-name-input"
                  />
                </div>
                <div>
                  <Label>{t('doctorEmail')}</Label>
                  <Input
                    type="email"
                    value={formData.doctor_email}
                    onChange={(e) => setFormData({ ...formData, doctor_email: e.target.value })}
                    placeholder="doctor@hospital.com"
                    required
                    data-testid="access-doctor-email-input"
                  />
                </div>
                <div>
                  <Label>{t('expiryHours')}</Label>
                  <Input
                    type="number"
                    value={formData.expiry_hours}
                    onChange={(e) => setFormData({ ...formData, expiry_hours: parseInt(e.target.value) })}
                    min={1}
                    max={72}
                    required
                    data-testid="expiry-hours-input"
                  />
                </div>
                <div>
                  <Label className="mb-3 block">Select Data to Share</Label>
                  <div className="space-y-2">
                    {accessSections.map((section) => (
                      <div key={section.id} className="flex items-center gap-2">
                        <Checkbox
                          id={section.id}
                          checked={formData.access_sections.includes(section.id)}
                          onCheckedChange={() => toggleSection(section.id)}
                          data-testid={`section-${section.id}`}
                        />
                        <Label htmlFor={section.id} className="cursor-pointer">{section.label}</Label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                    {t('cancel')}
                  </Button>
                  <Button type="submit" className="flex-1 bg-[#0D2340]" data-testid="generate-code-btn">
                    {t('generateQR')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* QR Code Display Dialog */}
        <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
          <DialogContent className="max-w-md text-center">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">{t('accessCode')}</DialogTitle>
            </DialogHeader>
            {selectedAccess && (
              <div className="space-y-6">
                <div className="qr-container mx-auto inline-block">
                  <QRCodeSVG
                    value={getPortalUrl(selectedAccess.access_code)}
                    size={200}
                    level="H"
                    includeMargin
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-slate-500">{t('accessCode')}</p>
                  <div className="flex items-center justify-center gap-2">
                    <code className="text-2xl font-bold font-mono bg-slate-100 px-4 py-2 rounded-lg">
                      {selectedAccess.access_code}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => copyAccessCode(selectedAccess.access_code)}
                      data-testid="copy-code-btn"
                    >
                      <Copy className="h-5 w-5" />
                    </Button>
                  </div>
                  <p className="text-sm text-slate-500">
                    {t('expiresAt')}: {new Date(selectedAccess.expires_at).toLocaleString()}
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm font-medium mb-2">Doctor Portal URL:</p>
                  <code className="text-xs break-all text-[#0D2340]">
                    {getPortalUrl(selectedAccess.access_code)}
                  </code>
                </div>
                
                {/* Share Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      const url = getPortalUrl(selectedAccess.access_code);
                      const message = language === 'hi' 
                        ? `नमस्ते डॉक्टर, मेरी स्वास्थ्य रिपोर्ट देखने के लिए इस लिंक पर क्लिक करें:\n\n${url}\n\nकोड: ${selectedAccess.access_code}\n\n(यह लिंक ${selectedAccess.expiry_hours || 24} घंटे में समाप्त हो जाएगा)`
                        : `Hello Doctor, please view my health records using this link:\n\n${url}\n\nCode: ${selectedAccess.access_code}\n\n(This link expires in ${selectedAccess.expiry_hours || 24} hours)`;
                      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
                    }}
                    className="bg-[#25D366] text-white hover:bg-[#25D366]/90 border-0"
                    data-testid="share-whatsapp-btn"
                  >
                    <MessageCircle className="h-5 w-5 mr-2" />
                    WhatsApp
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => copyAccessCode(selectedAccess.access_code)}
                    data-testid="copy-code-btn-2"
                  >
                    <Copy className="h-5 w-5 mr-2" />
                    {language === 'hi' ? 'कोड कॉपी करें' : 'Copy Code'}
                  </Button>
                </div>
                
                <Button
                  onClick={() => setQrDialogOpen(false)}
                  className="w-full bg-[#0D2340]"
                >
                  {language === 'hi' ? 'हो गया' : 'Done'}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Info Card */}
        <Card className="bg-[#0D2340]/5 border-[#0D2340]/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-[#0D2340] mt-0.5" />
              <div>
                <p className="font-medium text-[#0D2340]">{t('readOnlyAccess')}</p>
                <p className="text-sm text-slate-600">
                  Doctors can only view your selected health records. They cannot modify any data.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-10">{t('loading')}</div>
        ) : entries.length === 0 ? (
          <Card className="text-center py-10">
            <CardContent>
              <QrCode className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">{t('noData')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {entries.map((entry) => (
              <Card
                key={entry.id}
                className={`card-interactive ${isExpired(entry.expires_at) ? 'opacity-60' : ''}`}
                data-testid={`access-card-${entry.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg font-['Manrope']">{entry.doctor_name}</h3>
                        <Badge variant={isExpired(entry.expires_at) ? 'secondary' : 'default'} className={!isExpired(entry.expires_at) ? 'bg-[#2E7D32]' : ''}>
                          {isExpired(entry.expires_at) ? 'Expired' : 'Active'}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500 mb-2">{entry.doctor_email}</p>
                      <div className="flex items-center gap-2 mb-2">
                        <code className="text-sm bg-slate-100 px-2 py-1 rounded font-mono">
                          {entry.access_code}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyAccessCode(entry.access_code)}
                          className="h-8 px-2"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="h-4 w-4 text-slate-400" />
                        <span className={isExpired(entry.expires_at) ? 'text-red-500' : 'text-slate-500'}>
                          {formatExpiry(entry.expires_at)}
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1">
                        {entry.access_sections.map((section) => (
                          <Badge key={section} variant="outline" className="text-xs">
                            {section.replace('_', ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedAccess(entry);
                          setQrDialogOpen(true);
                        }}
                        data-testid={`view-qr-${entry.id}`}
                      >
                        <QrCode className="h-5 w-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRevoke(entry.id)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`revoke-${entry.id}`}
                      >
                        <Trash2 className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DoctorAccessPage;
